import * as React from 'react';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { User } from '../../../modules/auth/context/auth.provider';
import { RoleType, userRole } from '../../../modules/auth/common/role';
import { useAuth, useUser } from '../../../modules/auth/context/auth.consumer';
import { Page, SystemMenu } from '../AppContainer';
import { Outlet, useNavigate } from 'react-router-dom';
import { Footer } from '../Footer';
import { MenuItem } from '@mui/material';
import {
  Apartment,
  AssignmentInd,
  AutoAwesome,
  Build,
  Business,
  FilePresent,
  Logout,
  Person,
  QuestionAnswer,
  School,
  Settings,
  Work,
} from '@mui/icons-material';
import Container from '@mui/material/Container';
import { LogoSideBar, ToolbarSideBar } from './style';
import Menu from '@mui/material/Menu';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import { brand } from 'themes/brand';

const drawerWidth = 240;

const sectionTitleSx = {
  marginTop: '2rem',
  marginLeft: '0.5rem',
  fontWeight: '600',
  fontSize: '0.75rem',
  letterSpacing: '0.08em',
  color: brand.textSecondary,
};

interface AppContainerV2 {
  children: React.ReactNode;
  pages: Page[];
  menus: SystemMenu[];
}

export const AppContainerV2: React.FC<AppContainerV2> = ({
  children,
  pages,
  menus,
}) => {
  const user: User | null = useUser();
  const { logout } = useAuth();

  const role: RoleType = userRole(user as User);
  const navigate = useNavigate();

  const availablePages: Page[] = pages.filter((page) =>
    page.roles.includes(role),
  );

  const availableMenus: SystemMenu[] = menus.filter((menu: SystemMenu) =>
    menu.roles.includes(role),
  );

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLoggout = () => {
    logout().then(() => navigate('login'));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', flex: 1 }}>
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background: brand.navyGradient,
            borderBottom: `1px solid ${brand.glowBorder}`,
            boxShadow: brand.glowShadow,
          }}
        >
          <ToolbarSideBar disableGutters>
            <LogoSideBar onClick={() => navigate('/')} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexFlow: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Typography sx={{ color: 'white' }}>
                  Nível de acesso: <strong>{user?.role.toUpperCase()}</strong>
                </Typography>
                <Typography sx={{ color: 'white' }}>
                  Usuário: <strong>{user?.name}</strong>
                </Typography>
              </Box>
              <Tooltip title="Perfil">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{ ml: 2 }}
                  aria-controls={open ? 'account-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  <Avatar
                    alt={`${user?.name}`}
                    src={`${user?.profilePicture}`}
                  />
                </IconButton>
              </Tooltip>
            </Box>

            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    '&::before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={handleLoggout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </ToolbarSideBar>
        </AppBar>
        <Box
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            boxSizing: 'border-box',
            backgroundColor: brand.white,
            borderRight: `1px solid ${brand.border}`,
            '& .MuiListItemText-primary': { color: brand.text },
            '& .MuiListItemIcon-root': { color: brand.textSecondary },
            '& .MuiListItemButton-root:hover': { backgroundColor: brand.tint },
          }}
        >
          <Toolbar key={'toolbar-principal'} disableGutters />
          <Box sx={{ overflow: 'auto' }}>
            {availableMenus.map((menu: SystemMenu, index: number) => {
              switch (menu.label) {
                case 'GERENCIAMENTO E CADASTROS':
                  return (
                    <List key={index * 10}>
                      <Typography
                        key={'list-title-gerenciamento'}
                        sx={sectionTitleSx}
                      >
                        GERENCIAMENTO E CADASTROS
                      </Typography>

                      {availablePages.map((page: Page, indexPage: number) => (
                        <ListItem key={indexPage} disablePadding>
                          <ListItemButton>
                            <ListItemIcon>
                              {page.label === 'Setores' ? (
                                <Apartment />
                              ) : page.label === 'Instrumentos' ? (
                                <Build />
                              ) : page.label === 'Cargos' ? (
                                <Work />
                              ) : page.label === 'Empresas' ? (
                                <Business />
                              ) : page.label === 'Usuários' ? (
                                <Person />
                              ) : page.label === 'Arquivos' ? (
                                <FilePresent />
                              ) : page.label === 'Universidade MS' ? (
                                <School />
                              ) : (
                                <School />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              onClick={() => navigate(page.route)}
                              primary={page.label}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  );

                case 'AVALIAÇÕES': {
                  const canSeeProvas = [
                    RoleType.ADMIN,
                    RoleType.CONSULTOR,
                  ].includes(role);

                  if (!canSeeProvas) {
                    return (
                      <React.Fragment key={`avaliacoes-section-${index}`}>
                        <Divider />
                        <List>
                          <ListItem key={'avaliacoes-menu'} disablePadding>
                            <ListItemButton>
                              <ListItemIcon>{<AssignmentInd />}</ListItemIcon>
                              <ListItemText
                                onClick={() => navigate('/solicitacoes')}
                                primary={'Avaliações'}
                              />
                            </ListItemButton>
                          </ListItem>
                        </List>
                      </React.Fragment>
                    );
                  }

                  return (
                    <React.Fragment key={`solicitacoes-section-${index}`}>
                      <Divider />
                      <Typography sx={sectionTitleSx}>SOLICITAÇÕES</Typography>
                      <List>
                        <ListItem key={'avaliacoes-menu'} disablePadding>
                          <ListItemButton>
                            <ListItemIcon>{<AssignmentInd />}</ListItemIcon>
                            <ListItemText
                              onClick={() => navigate('/solicitacoes')}
                              primary={'Avaliações'}
                            />
                          </ListItemButton>
                        </ListItem>
                        <ListItem key={'provas-menu'} disablePadding>
                          <ListItemButton>
                            <ListItemIcon>{<QuestionAnswer />}</ListItemIcon>
                            <ListItemText
                              onClick={() => navigate('/provas')}
                              primary={'Provas'}
                            />
                          </ListItemButton>
                        </ListItem>
                      </List>
                    </React.Fragment>
                  );
                }

                case 'PROVAS':
                  return null;

                case 'ARQUIVOS':
                  return (
                    <>
                      <Divider />

                      <List>
                        <ListItem key={'arquivos-menu'} disablePadding>
                          <ListItemButton>
                            <ListItemIcon>{<FilePresent />}</ListItemIcon>
                            <ListItemText
                              onClick={() => navigate('/arquivos')}
                              primary={menu.label}
                            />
                          </ListItemButton>
                        </ListItem>
                      </List>
                    </>
                  );

                case 'UNIVERSIDADE MS':
                  return (
                    <>
                      <Divider />

                      <List>
                        <ListItem
                          key={'universidade-ms-analista-menu'}
                          disablePadding
                        >
                          <ListItemButton>
                            <ListItemIcon>{<School />}</ListItemIcon>
                            <ListItemText
                              onClick={() => navigate('/universidade-ms')}
                              primary={menu.label}
                            />
                          </ListItemButton>
                        </ListItem>
                      </List>
                    </>
                  );

                case 'JOBFIT':
                  return (
                    <React.Fragment key={`jobfit-section-${index}`}>
                      <Divider />
                      <List>
                        <ListItem key={'jobfit-menu'} disablePadding>
                          <ListItemButton onClick={() => navigate('/jobfit')}>
                            <ListItemIcon>{<AutoAwesome />}</ListItemIcon>
                            <ListItemText primary="JobFit MS" />
                            <Chip
                              label="NOVO"
                              size="small"
                              color="primary"
                              sx={{
                                height: 20,
                                fontSize: '10px',
                                fontWeight: 700,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      </List>
                    </React.Fragment>
                  );

                case 'ADMINISTRAÇÃO DO SISTEMA':
                  return (
                    <React.Fragment key={`admin-section-${index}`}>
                      <Divider />
                      <Typography sx={sectionTitleSx}>
                        ADMINISTRAÇÃO DO SISTEMA
                      </Typography>
                      <List>
                        <ListItem key={'configuracoes-menu'} disablePadding>
                          <ListItemButton>
                            <ListItemIcon>{<Settings />}</ListItemIcon>
                            <ListItemText
                              onClick={() => navigate('/configuracoes')}
                              primary={'Configurações'}
                            />
                          </ListItemButton>
                        </ListItem>
                      </List>
                    </React.Fragment>
                  );
              }
            })}
          </Box>
        </Box>
        <Box
          component="main"
          sx={{ flexGrow: 1, p: '3px 3x 0 0', backgroundColor: brand.surface }}
        >
          <Toolbar key={'toolbar-main-principal'} disableGutters />
          <Container sx={{ my: 2 }}>{children}</Container>
          <Outlet />
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};
